import { useAuthenticatedUser } from '../hooks/useAuth.js';
import { useMemo } from 'react';
import type {
    StyleForgeExtractResponse,
    StyleForgeGenerateRequest,
    StyleForgeGenerateResponse,
    StyleForgeSavedStyle,
    StyleForgeSaveStyleRequest,
} from '../types/styleForge';

export class ApiService {
    private baseUrl: string;
    private getToken: () => Promise<string>;

    constructor(baseUrl: string, getToken: () => Promise<string>) {
        this.baseUrl = baseUrl;
        this.getToken = getToken;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const token = await this.getToken();

        const url = `${this.baseUrl}${endpoint}`;
        const config: RequestInit = {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...options.headers,
            },
        };

        const response = await fetch(url, config);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API Error: ${response.status} - ${errorText}`);
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        }

        return response.text() as unknown as T;
    }

    async get<T>(endpoint: string, options: Pick<RequestInit, 'signal'> = {}): Promise<T> {
        return this.request<T>(endpoint, { ...options, method: 'GET' });
    }

    async getForBlob(endpoint: string, options: Pick<RequestInit, 'signal'> = {}, expectedContentType = 'application/pdf'): Promise<Blob> {
        const token = await this.getToken();
        const response = await fetch(`${this.baseUrl}${endpoint}`, {
            ...options, method: 'GET',
            headers: { 'Authorization': `Bearer ${token}`, 'Accept': expectedContentType },
        });
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        if (response.headers.get('content-type')?.split(';')[0].trim().toLowerCase() !== expectedContentType.toLowerCase()) {
            throw new Error('The server did not return the requested file type.');
        }
        return response.blob();
    }

    async patch<T>(endpoint: string, data: unknown): Promise<T> {
        return this.request<T>(endpoint, { method: "PATCH", body: JSON.stringify(data) });
    }

    async post<T>(endpoint: string, data: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async postForBlob(endpoint: string, data: any): Promise<Blob> {
        const token = await this.getToken();
        const url = `${this.baseUrl}${endpoint}`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API Error: ${response.status} - ${errorText}`);
        }

        return await response.blob();
    }

    async postFormData<T>(endpoint: string, formData: FormData, onProgress?: (percent: number) => void): Promise<T> {
        const token = await this.getToken();

        const url = `${this.baseUrl}${endpoint}`;
        if (onProgress) {
            return new Promise<T>((resolve, reject) => {
                const request = new XMLHttpRequest();
                request.open('POST', url);
                request.timeout = 120000;
                request.setRequestHeader('Authorization', `Bearer ${token}`);
                request.upload.onprogress = event => { if (event.lengthComputable) onProgress(Math.round(event.loaded / event.total * 100)); };
                request.onload = () => {
                    if (request.status < 200 || request.status >= 300) { reject(new Error(`API Error: ${request.status} - ${request.responseText}`)); return; }
                    try { resolve(JSON.parse(request.responseText)); } catch { reject(new Error('The upload response could not be read. Retry the upload.')); }
                };
                request.onerror = () => reject(new Error('The upload failed. Check your connection and retry.'));
                request.ontimeout = () => reject(new Error('The upload timed out. Retry or remove this screenshot.'));
                request.send(formData);
            });
        }
        const config: RequestInit = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                // Don't set Content-Type for FormData, let browser set it with boundary
            },
            body: formData,
        };

        const response = await fetch(url, config);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API Error: ${response.status} - ${errorText}`);
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        }

        return response.text() as unknown as T;
    }

    async put<T>(endpoint: string, data: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async delete<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'DELETE' });
    }
}

export function useApiService() {
    const { getToken } = useAuthenticatedUser();

    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    // Memoize the ApiService instance so components depending on it don't refire effects on every render
    const service = useMemo(() => new ApiService(apiUrl, getToken), [apiUrl, getToken]);
    return service;
}

// Azure Functions API Service for calling the Functions app
export class AzureFunctionsApiService {
    private baseUrl: string;
    private getToken: () => Promise<string>;

    constructor(baseUrl: string, getToken: () => Promise<string>) {
        this.baseUrl = baseUrl;
        this.getToken = getToken;
    }

    // Make getToken accessible for blob downloads
    public async getAuthToken(): Promise<string> {
        return await this.getToken();
    }

    /**
     * Unwraps Azure Functions IActionResult responses that come wrapped in Result objects
     * Azure Functions serialize IActionResult as { Value: T, StatusCode: number, ... }
     * or sometimes just as { Value: T } for successful responses
     */
    private unwrapResponse<T>(response: any): T {
        // Check if response is wrapped (has Value property)
        if (response && typeof response === 'object' && 'Value' in response) {
            console.log('[AzureFunctionsApiService] Unwrapping Azure Functions Result object');
            
            // Check for error responses if StatusCode is present
            if ('StatusCode' in response && response.StatusCode >= 400) {
                const errorValue = response.Value;
                const errorMessage = errorValue?.error || errorValue?.message || 'Unknown error';
                console.error('[AzureFunctionsApiService] Error response:', {
                    statusCode: response.StatusCode,
                    error: errorMessage,
                    value: errorValue
                });
                throw new Error(`${errorMessage} (Status: ${response.StatusCode})`);
            }
            
            // Return the unwrapped Value (works for both success with StatusCode and without)
            console.log('[AzureFunctionsApiService] Successfully unwrapped response Value');
            return response.Value as T;
        }
        
        // Response is not wrapped, return as-is
        console.log('[AzureFunctionsApiService] Response not wrapped, returning as-is');
        return response as T;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        let token: string;
        try {
            token = await this.getToken();
            console.log('[AzureFunctionsApiService] Token obtained successfully for', endpoint);
        } catch (error) {
            console.error('[AzureFunctionsApiService] Failed to get auth token:', error);
            throw new Error('Failed to obtain authentication token. Please try logging in again.');
        }

        const url = `${this.baseUrl}${endpoint}`;
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
        
        const config: RequestInit = {
            ...options,
            headers: {
                ...headers,
                ...(options.headers as Record<string, string> || {}),
            },
        };

        console.log('[AzureFunctionsApiService] Making request to:', url);
        console.log('[AzureFunctionsApiService] Authorization header set:', !!token);

        const response = await fetch(url, config);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('[AzureFunctionsApiService] Request failed:', {
                url,
                status: response.status,
                statusText: response.statusText,
                error: errorText
            });
            throw new Error(`Azure Functions API Error: ${response.status} - ${errorText}`);
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            const jsonResponse = await response.json();
            console.log('[AzureFunctionsApiService] Raw JSON response:', jsonResponse);
            
            try {
                // Unwrap the response if it's an Azure Functions IActionResult
                const unwrapped = this.unwrapResponse<T>(jsonResponse);
                console.log('[AzureFunctionsApiService] Unwrapped response:', unwrapped);
                return unwrapped;
            } catch (unwrapError) {
                // If unwrapping throws an error (e.g., 401), re-throw it with context
                console.error('[AzureFunctionsApiService] Unwrap error:', unwrapError);
                throw unwrapError;
            }
        }

        return response.text() as unknown as T;
    }

    async post<T>(endpoint: string, data: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async put<T>(endpoint: string, data: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
        let token: string;
        try {
            token = await this.getToken();
            console.log('[AzureFunctionsApiService] Token obtained successfully for FormData request to', endpoint);
            console.log('[AzureFunctionsApiService] Token length:', token?.length);
            console.log('[AzureFunctionsApiService] Token prefix:', token?.substring(0, 30) + '...');
        } catch (error) {
            console.error('[AzureFunctionsApiService] Failed to get auth token for FormData request:', error);
            throw new Error('Failed to obtain authentication token. Please try logging in again.');
        }

        const url = `${this.baseUrl}${endpoint}`;
        const config: RequestInit = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                // Don't set Content-Type for FormData, let browser set it with boundary
            },
            body: formData,
        };

        console.log('[AzureFunctionsApiService] Making FormData request to:', url);
        console.log('[AzureFunctionsApiService] Authorization header set:', !!token);

        const response = await fetch(url, config);

        console.log('[AzureFunctionsApiService] FormData response status:', response.status, response.statusText);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('[AzureFunctionsApiService] FormData request failed:', {
                url,
                status: response.status,
                statusText: response.statusText,
                error: errorText
            });
            throw new Error(`Azure Functions API Error: ${response.status} - ${errorText}`);
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            const jsonResponse = await response.json();
            console.log('[AzureFunctionsApiService] FormData raw JSON response:', jsonResponse);
            // Unwrap the response if it's an Azure Functions IActionResult
            return this.unwrapResponse<T>(jsonResponse);
        }

        return response.text() as unknown as T;
    }

    async get<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET' });
    }

    async delete<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'DELETE' });
    }
}

export function useAzureFunctionsApiService() {
    const { getToken } = useAuthenticatedUser();

    // Azure Functions URL from environment variables
    const functionsUrl = import.meta.env.VITE_AZURE_FUNCTIONS_URL || 'http://localhost:7071/api';
    // Memoize instance to avoid refiring effects using this service
    const service = useMemo(() => new AzureFunctionsApiService(functionsUrl, getToken), [functionsUrl, getToken]);
    return service;
}

// PDF Job related types and API methods
export interface PDFJobDTO {
    id: number;
    name: string;
    template: string;
    dataSource: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    createdAt: string;
    completedAt?: string;
    outputCount: number;
    errorCount: number;
    instanceId?: string; // Azure Functions orchestration instance ID
}

export interface CreatePDFJobRequest {
    name: string;
    template: string;
    dataFile: File;
    outputFormat?: string;
    cultures?: string[];
}

// Azure Functions PDF Job API types
export interface AzureFunctionsPDFJob {
    id: number;
    name: string;
    template: string;
    dataSource: string;
    status: string;
    progress: number;
    createdAt: string;
    completedAt?: string;
    outputCount: number;
    errorCount: number;
    cultures?: string[];
}

export interface BulkPDFGenerationResponse {
    instanceId: string;
    statusQueryGetUri: string;
    message: string;
    pdfJob: {
        id: number;
        name: string;
        template: string;
        dataSource: string;
        status: string;
        originalFileName?: string;
        storedFileName?: string;
        cultures?: string[];
    };
    user: string;
    userEmail?: string;
    timestamp: string;
}

export interface OrchestrationStatus {
    instanceId: string;
    runtimeStatus: string;
    input: any;
    output: any;
    createdAt: string;
    lastUpdatedAt: string;
}

export interface TerminateAllWorkflowsResponse {
    message: string;
    summary: {
        totalJobsProcessed: number;
        jobsMarkedAsTerminated: number;
        jobsNotFound: number;
        errorCount: number;
    };
    terminationResults: any[];
    errors?: string[];
    user: string;
    reason: string;
    timestamp: string;
    note?: string;
}

export function usePDFJobApi() {
    const apiService = useApiService();

    return {
        // Get all PDF jobs
        getPDFJobs: (): Promise<PDFJobDTO[]> =>
            apiService.get<PDFJobDTO[]>('/PDFJob'),

        // Get specific PDF job
        getPDFJob: (id: number): Promise<PDFJobDTO> =>
            apiService.get<PDFJobDTO>(`/PDFJob/${id}`),

        // Create PDF job with file upload
        createPDFJobWithFile: async (request: CreatePDFJobRequest): Promise<PDFJobDTO> => {
            const formData = new FormData();
            formData.append('name', request.name);
            formData.append('template', request.template);
            formData.append('dataFile', request.dataFile);
            if (request.outputFormat) {
                formData.append('outputFormat', request.outputFormat);
            }
            if (request.cultures && request.cultures.length > 0) {
                formData.append('cultures', request.cultures.join(','));
            }

            return apiService.postFormData<PDFJobDTO>('/PDFJob/create-with-file', formData);
        },

        // Create PDF job (without file upload)
        createPDFJob: (job: Omit<PDFJobDTO, 'id' | 'createdAt' | 'completedAt'>): Promise<PDFJobDTO> =>
            apiService.post<PDFJobDTO>('/PDFJob', job),

        // Update PDF job
        updatePDFJob: (id: number, job: PDFJobDTO): Promise<PDFJobDTO> =>
            apiService.put<PDFJobDTO>(`/PDFJob/${id}`, job),

        // Delete PDF job
        deletePDFJob: (id: number): Promise<void> =>
            apiService.delete<void>(`/PDFJob/${id}`),
    };
}

// Azure Functions PDF Job API methods
export function useAzureFunctionsPDFJobApi() {
    const functionsApiService = useAzureFunctionsApiService();

    return {
        // Start bulk PDF generation using Azure Functions (JSON-based)
        generateBulkPdfs: (pdfJob: AzureFunctionsPDFJob): Promise<BulkPDFGenerationResponse> =>
            functionsApiService.post<BulkPDFGenerationResponse>('/GenerateBulkPdfs', pdfJob),

        // Start bulk PDF generation with file upload using Azure Functions
        generateBulkPdfsWithFileUpload: async (
            name: string,
            template: string,
            dataFile: File,
            outputFormat?: string,
            cultures?: string[]
        ): Promise<BulkPDFGenerationResponse> => {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('template', template);
            formData.append('dataFile', dataFile);
            if (outputFormat) {
                formData.append('outputFormat', outputFormat);
            }
            if (cultures && cultures.length > 0) {
                formData.append('cultures', cultures.join(','));
            }

            return functionsApiService.postFormData<BulkPDFGenerationResponse>('/GenerateBulkPdfsWithFileUpload', formData);
        },

        // Get orchestration status
        getOrchestrationStatus: (instanceId: string): Promise<OrchestrationStatus> =>
            functionsApiService.get<OrchestrationStatus>(`/orchestrators/${instanceId}`),

        // Terminate all open workflows
        terminateAllOpenWorkflows: (reason?: string): Promise<TerminateAllWorkflowsResponse> =>
            functionsApiService.post<TerminateAllWorkflowsResponse>('/TerminateAllOpenWorkflows', { reason: reason || 'Terminated by user request' }),

        // Terminate specific workflow by instance ID
        terminateWorkflow: (instanceId: string, reason?: string): Promise<any> =>
            functionsApiService.post<any>(`/orchestrators/${instanceId}/terminate`, { reason: reason || 'Terminated by user request' }),

        // Get all active workflows
        getAllActiveWorkflows: (): Promise<any> =>
            functionsApiService.get<any>('/GetAllActiveWorkflows'),

        // Purge workflow history
        purgeWorkflowHistory: (options?: {
            olderThanDays?: number;
            includeCompleted?: boolean;
            includeFailed?: boolean;
            includeTerminated?: boolean;
            purgeDatabaseRecords?: boolean;
        }): Promise<any> =>
            functionsApiService.post<any>('/PurgeWorkflowHistory', options || {}),

        // Test CORS endpoint
        testCors: (): Promise<any> =>
            functionsApiService.get<any>('/CorsTest'),

        // Test file upload endpoint (no auth)
        testFileUpload: async (dataFile: File): Promise<any> => {
            const formData = new FormData();
            formData.append('dataFile', dataFile);

            // Get the Azure Functions URL from environment variables
            const azureFunctionsUrl = import.meta.env.VITE_AZURE_FUNCTIONS_URL || 'http://localhost:7071/api';

            // Use direct fetch for test endpoint to bypass authentication
            const response = await fetch(`${azureFunctionsUrl}/TestFileUpload`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Test File Upload Error: ${response.status} - ${errorText}`);
            }

            return await response.json();
        },

        // Get download URL for ZIP file
        getPdfZipDownloadUrl: (jobId: number): Promise<any> =>
            functionsApiService.get<any>(`/pdf-jobs/${jobId}/download-url`),

        // Download ZIP file directly
        downloadPdfZip: async (jobId: number): Promise<Blob> => {
            const token = await functionsApiService.getAuthToken();
            const functionsUrl = import.meta.env.VITE_AZURE_FUNCTIONS_URL || 'http://localhost:7071/api';

            const response = await fetch(`${functionsUrl}/pdf-jobs/${jobId}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Download failed: ${response.status} - ${errorText}`);
            }

            return await response.blob();
        },
    };
}

// Demand-specific API methods
export interface DemandDTO {
    id?: string;
    title: string;
    description: string;
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    department: string;
    requestedBy: string;
    businessCase: string;
    estimatedCost: number;
    status?: 'New' | 'Under Review' | 'Approved' | 'Rejected' | 'On Hold';
    submittedDate?: string;
}

export function useDemandApi() {
    const apiService = useApiService();

    return {
        getDemands: () => apiService.get<DemandDTO[]>('/demands'),
        getDemand: (id: string) => apiService.get<DemandDTO>(`/demands/${id}`),
        createDemand: (demand: DemandDTO) => apiService.post<DemandDTO>('/demands', demand),
        updateDemand: (id: string, demand: DemandDTO) => apiService.put<DemandDTO>(`/demands/${id}`, demand),
        deleteDemand: (id: string) => apiService.delete<void>(`/demands/${id}`),
        approveDemand: (id: string) => apiService.post<void>(`/demands/${id}/approve`, {}),
        rejectDemand: (id: string, reason: string) => apiService.post<void>(`/demands/${id}/reject`, { reason }),
    };
}

// Weather forecast API methods
export interface WeatherForecast {
    date: string;
    temperatureC: number;
    temperatureF: number;
    summary: string;
}

// Campaign API types and methods
export interface OnlinePlatformOption {
    id: number;
    name: string;
    sortOrder: number;
}

export interface TargetProfileOption {
    id: number;
    audience: string;
    name: string;
    description?: string;
    parentId?: number | null;
    sortOrder: number;
    children: TargetProfileOption[];
}

export interface BrandSummary {
    id: number;
    name: string;
    tagline?: string | null;
    logoUrl?: string | null;
}

export interface ToneExample {
    context?: string | null;
    exampleText: string;
    order: number;
}

export interface ToneProfileDetail {
    archetype?: string | null;
    styleNotes?: string | null;
    doKeywords: string[];
    dontKeywords: string[];
    examples: ToneExample[];
}

export interface PillarMessageDetail {
    id?: number | null;
    message: string;
    order: number;
}

export interface PillarDetail {
    id?: number | null;
    name: string;
    priority?: number | null;
    messages: PillarMessageDetail[];
}

export interface ClaimDetail {
    id?: number | null;
    text: string;
    approved: boolean;
    expiryDate?: string | null;
    jurisdictions: string[];
}

export interface BrandAudienceDetail {
    id?: number | null;
    targetProfileOptionId: number;
    description?: string | null;
    valueDrivers: string[];
    objections: string[];
}

export interface ChannelGuideDetail {
    id?: number | null;
    name: string;
    toneOverride?: string | null;
    lengthGuidance?: string | null;
    callToActions: string[];
}

export interface BrandDetail {
    id?: number | null;
    name: string;
    tagline?: string | null;
    promise?: string | null;
    valueProposition?: string | null;
    logoUrl?: string | null;
    nameVariants: { locale: string; shortName: string; longName?: string | null; phonetic?: string | null }[];
    values: string[];
    differentiators: { id?: number | null; text: string; order: number }[];
    tone: ToneProfileDetail;
    pillars: PillarDetail[];
    approvedPhrases: string[];
    bannedPhrases: { id?: number | null; phrase: string; reason?: string | null }[];
    storylines: { id?: number | null; title: string; order: number }[];
    disclaimers: string[];
    claims: ClaimDetail[];
    culturalSensitivities: string[];
    audiences: BrandAudienceDetail[];
    markets: string[];
    languages: string[];
    topics: string[];
    hashtags: string[];
    channels: ChannelGuideDetail[];
}

export interface CreateTargetProfileOptionRequest {
    name: string;
    audience: 'B2B' | 'B2C';
    description?: string;
    parentId?: number | null;
    sortOrder?: number;
}

export interface UpdateTargetProfileOptionRequest {
    name?: string;
    description?: string | null;
}

export interface CampaignReferenceData {
    onlinePlatforms: OnlinePlatformOption[];
    b2bTargetProfiles: TargetProfileOption[];
    b2cTargetProfiles: TargetProfileOption[];
    brands: BrandSummary[];
}

export interface CampaignDTO {
    id: number;
    name: string;
    businessUnit: string;
    brandId: number;
    brandName: string;
    category: string;
    concept: string;
    status: string;
    startDate: string;
    endDate: string;
    budget: number;
    targetProfile: string;
    campaignType: string;
    b2bTargetProfileIds: number[];
    b2cTargetProfileIds: number[];
    platforms: string[];
    products: string[];
    description: string;
    imageUrl: string;
    targetMarkets: string[];
    hubSpotFormIds: string[];
    revenueMatrix: string; // JSON string containing revenue matrix data
    websitePaths: string[];
    createdAt: string;
    updatedAt: string;
    createdBy: string;
}

export interface CreateCampaignRequest {
    name: string;
    businessUnit: string;
    brandId: number;
    category: string;
    concept?: string;
    startDate: string; // ISO date string
    endDate: string; // ISO date string
    budget: number;
    targetProfile: string;
    campaignType: string;
    b2bTargetProfileIds: number[];
    b2cTargetProfileIds: number[];
    platforms?: string[];
    products?: string[];
    description?: string;
    imageUrl?: string;
    targetMarkets?: string[];
    hubSpotFormIds?: string[];
    revenueMatrix?: string; // JSON string containing revenue matrix data
    websitePaths?: string[];
}

export interface UpdateCampaignRequest {
    name?: string;
    businessUnit?: string;
    brandId?: number;
    category?: string;
    concept?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    budget?: number;
    targetProfile?: string;
    campaignType?: string;
    b2bTargetProfileIds?: number[];
    b2cTargetProfileIds?: number[];
    platforms?: string[];
    products?: string[];
    description?: string;
    imageUrl?: string;
    targetMarkets?: string[];
    hubSpotFormIds?: string[];
    revenueMatrix?: string; // JSON string containing revenue matrix data
    websitePaths?: string[];
}

export interface CampaignBriefResponse {
    brief: string;
    success: boolean;
    errorMessage?: string;
    pdfUrl?: string;
}

export interface AdVariationResponse {
    imageBase64: string;
    mimeType: string;
    prompt: string;
    productItemNumber: string;
    productName: string;
    generatedAt: string;
    model: string;
    sourceImageFileName: string;
    warnings?: string[] | null;
}

export interface CampaignApi {
    getCampaigns: () => Promise<CampaignDTO[]>;
    getCampaign: (id: number) => Promise<CampaignDTO>;
    getReferenceData: () => Promise<CampaignReferenceData>;
    createCampaign: (campaign: CreateCampaignRequest) => Promise<CampaignDTO>;
    createCampaignWithImage: (campaign: CreateCampaignRequest, imageFile?: File) => Promise<CampaignDTO>;
    updateCampaign: (id: number, campaign: UpdateCampaignRequest) => Promise<CampaignDTO>;
    deleteCampaign: (id: number) => Promise<void>;
    getRecentActivity: () => Promise<CampaignDTO[]>;
    generateCampaignBrief: (id: number) => Promise<CampaignBriefResponse>;
    createTargetProfile: (request: CreateTargetProfileOptionRequest) => Promise<TargetProfileOption>;
    updateTargetProfile: (id: number, request: UpdateTargetProfileOptionRequest) => Promise<TargetProfileOption>;
    deleteTargetProfile: (id: number) => Promise<void>;
    generateAdVariation: (formData: FormData) => Promise<AdVariationResponse>;
    extractStyleReference: (formData: FormData) => Promise<StyleForgeExtractResponse>;
    generateStyledImage: (request: StyleForgeGenerateRequest) => Promise<StyleForgeGenerateResponse>;
    listSavedStyles: () => Promise<StyleForgeSavedStyle[]>;
    saveStyle: (request: StyleForgeSaveStyleRequest) => Promise<StyleForgeSavedStyle>;
    updateSavedStyle: (id: number, request: StyleForgeSaveStyleRequest) => Promise<StyleForgeSavedStyle>;
    deleteSavedStyle: (id: number) => Promise<void>;
}

export function useCampaignApi(): CampaignApi {
    const apiService = useApiService();

    // Memoize the API surface to keep a stable reference across renders
    return useMemo<CampaignApi>(() => ({
        // Get all campaigns
        getCampaigns: (): Promise<CampaignDTO[]> =>
            apiService.get<CampaignDTO[]>('/campaigns'),

        // Get specific campaign by ID
        getCampaign: (id: number): Promise<CampaignDTO> =>
            apiService.get<CampaignDTO>(`/campaigns/${id}`),

        // Load campaign reference data (platforms, target profiles)
        getReferenceData: (): Promise<CampaignReferenceData> =>
            apiService.get<CampaignReferenceData>('/campaigns/reference-data'),

        // Create new campaign
        createCampaign: (campaign: CreateCampaignRequest): Promise<CampaignDTO> =>
            apiService.post<CampaignDTO>('/campaigns', campaign),

        // Create new campaign with optional image upload (multipart/form-data)
        createCampaignWithImage: async (campaign: CreateCampaignRequest, imageFile?: File): Promise<CampaignDTO> => {
            const formData = new FormData();
            formData.append('name', campaign.name);
            formData.append('businessUnit', campaign.businessUnit);
            formData.append('brandId', String(campaign.brandId));
            formData.append('category', campaign.category);
            // Default to 'none' when concept is undefined to match server expectations
            formData.append('concept', campaign.concept ?? 'none');
            formData.append('startDate', campaign.startDate);
            formData.append('endDate', campaign.endDate);
            formData.append('budget', String(campaign.budget ?? 0));
            formData.append('targetProfile', campaign.targetProfile);
            formData.append('campaignType', campaign.campaignType);
            formData.append('b2bTargetProfileIds', JSON.stringify(campaign.b2bTargetProfileIds ?? []));
            formData.append('b2cTargetProfileIds', JSON.stringify(campaign.b2cTargetProfileIds ?? []));

            // Arrays should be JSON strings per server implementation
            if (campaign.platforms)
                formData.append('platforms', JSON.stringify(campaign.platforms));
            if (campaign.products)
                formData.append('products', JSON.stringify(campaign.products));
            if (campaign.targetMarkets)
                formData.append('targetMarkets', JSON.stringify(campaign.targetMarkets));
            if (campaign.hubSpotFormIds)
                formData.append('hubSpotFormIds', JSON.stringify(campaign.hubSpotFormIds));
            if (campaign.websitePaths)
                formData.append('websitePaths', JSON.stringify(campaign.websitePaths));

            if (campaign.description)
                formData.append('description', campaign.description);

            if (campaign.revenueMatrix)
                formData.append('revenueMatrix', campaign.revenueMatrix);

            if (imageFile) {
                formData.append('image', imageFile);
            }

            return apiService.postFormData<CampaignDTO>('/campaigns/create-with-image', formData);
        },

        // Update existing campaign
        updateCampaign: (id: number, campaign: UpdateCampaignRequest): Promise<CampaignDTO> =>
            apiService.put<CampaignDTO>(`/campaigns/${id}`, campaign),

        // Delete campaign
        deleteCampaign: (id: number): Promise<void> =>
            apiService.delete<void>(`/campaigns/${id}`),

        // Get recent campaign activity
        getRecentActivity: (): Promise<CampaignDTO[]> =>
            apiService.get<CampaignDTO[]>('/campaigns/recent-activity'),

        // Generate a campaign brief artefact using SmartAgents
        generateCampaignBrief: (id: number): Promise<CampaignBriefResponse> =>
            apiService.post<CampaignBriefResponse>(`/campaigns/${id}/generate-brief`, {}),

        generateAdVariation: (formData: FormData): Promise<AdVariationResponse> =>
            apiService.postFormData<AdVariationResponse>('/campaigns/ad-variations', formData),

        extractStyleReference: (formData: FormData): Promise<StyleForgeExtractResponse> =>
            apiService.postFormData<StyleForgeExtractResponse>('/campaigns/style-forge/extract-style', formData),

        generateStyledImage: (request: StyleForgeGenerateRequest): Promise<StyleForgeGenerateResponse> =>
            apiService.post<StyleForgeGenerateResponse>('/campaigns/style-forge/generate-image', request),

        listSavedStyles: (): Promise<StyleForgeSavedStyle[]> =>
            apiService.get<StyleForgeSavedStyle[]>('/campaigns/style-forge/styles'),

        saveStyle: (request: StyleForgeSaveStyleRequest): Promise<StyleForgeSavedStyle> =>
            apiService.post<StyleForgeSavedStyle>('/campaigns/style-forge/styles', request),

        updateSavedStyle: (id: number, request: StyleForgeSaveStyleRequest): Promise<StyleForgeSavedStyle> =>
            apiService.put<StyleForgeSavedStyle>(`/campaigns/style-forge/styles/${id}`, request),

        deleteSavedStyle: (id: number): Promise<void> =>
            apiService.delete<void>(`/campaigns/style-forge/styles/${id}`),

        // Target profile management helpers
        createTargetProfile: (request: CreateTargetProfileOptionRequest): Promise<TargetProfileOption> =>
            apiService.post<TargetProfileOption>('/target-profiles', request),

        updateTargetProfile: (id: number, request: UpdateTargetProfileOptionRequest): Promise<TargetProfileOption> =>
            apiService.put<TargetProfileOption>(`/target-profiles/${id}`, request),

        deleteTargetProfile: (id: number): Promise<void> =>
            apiService.delete<void>(`/target-profiles/${id}`),
    }), [apiService]);
}

export interface BrandApi {
    getBrands: () => Promise<BrandSummary[]>;
    getBrand: (id: number) => Promise<BrandDetail>;
    createBrand: (brand: BrandDetail) => Promise<BrandDetail>;
    updateBrand: (id: number, brand: BrandDetail) => Promise<BrandDetail>;
    deleteBrand: (id: number) => Promise<void>;
}

export function useBrandApi(): BrandApi {
    const apiService = useApiService();

    return useMemo<BrandApi>(() => ({
        getBrands: () => apiService.get<BrandSummary[]>('/brands'),
        getBrand: (id: number) => apiService.get<BrandDetail>(`/brands/${id}`),
        createBrand: (brand: BrandDetail) => apiService.post<BrandDetail>('/brands', brand),
        updateBrand: (id: number, brand: BrandDetail) => apiService.put<BrandDetail>(`/brands/${id}`, brand),
        deleteBrand: (id: number) => apiService.delete<void>(`/brands/${id}`)
    }), [apiService]);
}

// HubSpot Forms API types
export interface HubSpotForm {
    id: string;
    name: string;
    createdAt: string;
    portalId?: string;
    action?: string;
    method?: string;
    updatedAt: string;
}

export interface HubSpotFormField {
    name: string;
    label: string;
    fieldType: string;
    description?: string;
    required?: boolean;
    displayOrder?: number;
    hidden?: boolean;
    enabled?: boolean;
    defaultValue?: string;
    placeholder?: string;
    selectedOptions?: any[];
    options?: HubSpotFormFieldOption[];
}

export interface HubSpotFormFieldOption {
    label: string;
    value: string;
    displayOrder?: number;
    hidden?: boolean;
    description?: string;
    readOnly?: boolean;
    doubleData?: number;
}

export function useHubSpotFormsApi() {
    const apiService = useApiService();

    return {
        // Get all HubSpot forms
        getForms: (): Promise<HubSpotForm[]> =>
            apiService.get<HubSpotForm[]>('/hubspot/forms'),

        // Get specific form by ID
        getForm: (formId: string): Promise<HubSpotForm> =>
            apiService.get<HubSpotForm>(`/hubspot/forms/${formId}`),

        // Refresh all forms from HubSpot API
        refreshForms: (): Promise<{ success: boolean; formsProcessed: number; message: string }> =>
            apiService.post('/hubspot/forms/refresh', {}),

        // Get form fields with selection options
        getFormFields: (formId: string): Promise<HubSpotFormField[]> =>
            apiService.get<HubSpotFormField[]>(`/hubspot/forms/${formId}/fields`),
    };
}

// HubSpot Backfill API types and methods
export interface HubSpotBackfillRequest {
    startDate?: string; // ISO date string
    endDate?: string; // ISO date string
    chunkSize?: 'Month' | 'Year';
}

export interface HubSpotBackfillResponse {
    instanceId: string;
    message: string;
    statusQueryUri: string;
    request: HubSpotBackfillRequest;
}

export interface HubSpotBackfillStatus {
    instanceId: string;
    runtimeStatus: string;
    createdTime: string;
    lastUpdatedTime: string;
    input: any;
    output: any;
    customStatus: any;
    failureDetails?: any;
    orchestrationData?: {
        totalContacts: number;
        completedChunks: number;
        totalChunks: number;
        failedChunks: number;
        success: boolean;
        duration: number;
        startTime: string;
        endTime: string;
        errorMessage?: string;
        chunkResults?: Array<{
            startDate: string;
            endDate: string;
            contactsProcessed: number;
            success: boolean;
            duration: number;
            errorMessage?: string;
        }>;
    };
}

export interface HubSpotTerminateAllResponse {
    message: string;
    reason: string;
    summary: {
        totalFound: number;
        terminated: number;
        failed: number;
    };
    results: Array<{
        instanceId: string;
        status: string;
        message: string;
        createdTime: string;
        lastUpdatedTime: string;
    }>;
    user: string;
    timestamp: string;
}

export interface HubSpotTerminateResponse {
    message: string;
    instanceId: string;
    reason: string;
    previousStatus: string;
    terminatedAt: string;
    user: string;
    timestamp: string;
}

export function useHubSpotBackfillApi() {
    const functionsApiService = useAzureFunctionsApiService();

    return {
        // Start HubSpot backfill process
        startBackfill: (request: HubSpotBackfillRequest): Promise<HubSpotBackfillResponse> =>
            functionsApiService.post<HubSpotBackfillResponse>('/hubspot/backfill/start', request),

        // Get backfill status
        getBackfillStatus: (instanceId: string): Promise<HubSpotBackfillStatus> =>
            functionsApiService.get<HubSpotBackfillStatus>(`/hubspot/backfill/status/${instanceId}`),

        // Terminate all HubSpot backfill orchestrations
        terminateAllBackfills: (reason?: string): Promise<HubSpotTerminateAllResponse> =>
            functionsApiService.post<HubSpotTerminateAllResponse>('/hubspot/backfill/terminate-all', { 
                reason: reason || 'Terminated by user request' 
            }),

        // Terminate specific HubSpot backfill orchestrations
        terminateBackfill: (instanceId: string, reason?: string): Promise<HubSpotTerminateResponse> =>
            functionsApiService.post<HubSpotTerminateResponse>(`/hubspot/backfill/terminate/${instanceId}`, { 
                reason: reason || 'Terminated by user request' 
            }),
    };
}

export interface HubSpotEmailMetricsBackfillRequest {
    startDateUtc?: string;
}

export interface HubSpotEmailMetricsBackfillResponse {
    instanceId: string;
    startDateUtc: string;
    statusQueryUri: string;
    timestamp: string;
}

export interface HubSpotEmailMetricsBackfillStatus {
    instanceId: string;
    runtimeStatus: string;
    createdAt: string;
    lastUpdatedAt: string;
    output?: string;
    customStatus?: string;
    failureDetails?: any;
    timestamp: string;
}

export interface HubSpotEmailMetricsSyncResult {
    startTimeUtc: string;
    endTimeUtc: string;
    inserted: number;
    updated: number;
    totalProcessed: number;
    appliedSinceUtc?: string;
    lastSuccessfulSyncUtc?: string;
    success: boolean;
    errorMessage?: string;
}

export interface HubSpotEmailMetricsResetResult {
    resetAtUtc: string;
    deletedRows: number;
}

export interface HubSpotEmailMetricsState {
    id: number;
    lastSuccessfulSyncUtc?: string;
    backfillStartUtc?: string;
    backfillEndUtc?: string;
    isBackfillCompleted: boolean;
    isBackfillRunning: boolean;
    lastError?: string;
    updatedAtUtc: string;
}

export function useHubSpotEmailMetricsApi() {
    const functionsApiService = useAzureFunctionsApiService();

    return {
        startBackfill: (request: HubSpotEmailMetricsBackfillRequest): Promise<HubSpotEmailMetricsBackfillResponse> =>
            functionsApiService.post<HubSpotEmailMetricsBackfillResponse>('/hubspot/email-metrics/backfill/start', request),

        getBackfillStatus: (instanceId: string): Promise<HubSpotEmailMetricsBackfillStatus> =>
            functionsApiService.get<HubSpotEmailMetricsBackfillStatus>(`/hubspot/email-metrics/backfill/status/${instanceId}`),

        triggerUpdate: (request?: HubSpotEmailMetricsBackfillRequest): Promise<HubSpotEmailMetricsSyncResult> =>
            functionsApiService.post<HubSpotEmailMetricsSyncResult>('/hubspot/email-metrics/update/trigger', request || {}),

        resetMetrics: (): Promise<HubSpotEmailMetricsResetResult> =>
            functionsApiService.post<HubSpotEmailMetricsResetResult>('/hubspot/email-metrics/reset', {}),

        getState: (): Promise<HubSpotEmailMetricsState> =>
            functionsApiService.get<HubSpotEmailMetricsState>('/hubspot/email-metrics/state'),
    };
}

// Weather forecast API methods
export function useWeatherApi() {
    const { getToken } = useAuthenticatedUser();

    const getWeatherForecast = async (): Promise<WeatherForecast[]> => {
        try {
            // Get the token for authentication
            const token = await getToken();

            // Use direct fetch without the /api base URL since WeatherForecast controller
            // is routed at the root level (/weatherforecast), not under /api
            const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || '';
            const url = `${baseUrl}/weatherforecast`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Weather API Error: ${response.status} - ${errorText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching weather forecast:', error);
            throw error;
        }
    };

    return {
        getWeatherForecast,
    };
}

// Catalog Job related types and API methods
export interface CatalogJob {
    id: number;
    name: string;
    queryName: string;
    templateId: string;
    status: 'pending' | 'processing' | 'completed' | 'failed' | 'completed_with_errors';
    progress: number;
    itemCount: number;
    successCount: number;
    errorCount: number;
    outputUrl?: string;
    createdAt: string;
    completedAt?: string;
    instanceId?: string;
}

export interface StartCatalogGenerationRequest {
    name: string;
    queryName: string;
    templateId: string;
    entityIds: number[];
}

export interface StartCatalogGenerationResponse {
    instanceId: string;
    message: string;
    catalogJob: {
        id: number;
        name: string;
        status: string;
    };
    user: string;
    timestamp: string;
}

// Catalog Template types
export interface CatalogTemplate {
    id: number;
    name: string;
    description?: string;
    blobUrl: string;
    fileName: string;
    uniqueFileName: string;
    fileSize: number;
    uploadedAt: string;
    uploadedBy?: string;
    isActive: boolean;
    tags?: string[];
    usageCount: number;
}

// Azure Functions Catalog API methods
export function useAzureFunctionsCatalogApi() {
    const functionsApiService = useAzureFunctionsApiService();

    return {
        // Start catalog generation
        startCatalogGeneration: (request: StartCatalogGenerationRequest): Promise<StartCatalogGenerationResponse> =>
            functionsApiService.post<StartCatalogGenerationResponse>('/catalog/generate', request),

        // Get all catalog jobs
        getCatalogJobs: (): Promise<CatalogJob[]> =>
            functionsApiService.get<CatalogJob[]>('/catalog/jobs'),

        // Get specific catalog job
        getCatalogJob: (jobId: number): Promise<CatalogJob> =>
            functionsApiService.get<CatalogJob>(`/catalog/jobs/${jobId}`),

        // Download catalog PowerPoint
        downloadCatalog: async (jobId: number): Promise<Blob> => {
            const token = await functionsApiService.getAuthToken();
            const functionsUrl = import.meta.env.VITE_AZURE_FUNCTIONS_URL || 'http://localhost:7071/api';

            const response = await fetch(`${functionsUrl}/catalog/jobs/${jobId}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Download failed: ${response.status} - ${errorText}`);
            }

            return await response.blob();
        },

        // Delete catalog job
        deleteCatalogJob: (jobId: number): Promise<void> =>
            functionsApiService.delete<void>(`/catalog/jobs/${jobId}`),

        // Template management methods
        uploadTemplate: async (name: string, description: string | undefined, templateFile: File, tags?: string[]): Promise<CatalogTemplate> => {
            const formData = new FormData();
            formData.append('name', name);
            if (description) {
                formData.append('description', description);
            }
            if (tags && tags.length > 0) {
                formData.append('tags', JSON.stringify(tags));
            }
            formData.append('templateFile', templateFile);

            return functionsApiService.postFormData<CatalogTemplate>('/catalog/templates/upload', formData);
        },

        getTemplates: (): Promise<CatalogTemplate[]> =>
            functionsApiService.get<CatalogTemplate[]>('/catalog/templates'),

        getTemplate: (id: number): Promise<CatalogTemplate> =>
            functionsApiService.get<CatalogTemplate>(`/catalog/templates/${id}`),

        deleteTemplate: (id: number, hardDelete: boolean = false): Promise<void> =>
            functionsApiService.delete<void>(`/catalog/templates/${id}?hardDelete=${hardDelete}`),

        updateTemplate: (id: number, updates: Partial<CatalogTemplate>): Promise<CatalogTemplate> => {
            return functionsApiService.put<CatalogTemplate>(`/catalog/templates/${id}`, updates);
        },

        searchTemplates: (searchTerm: string): Promise<CatalogTemplate[]> =>
            functionsApiService.get<CatalogTemplate[]>(`/catalog/templates?search=${encodeURIComponent(searchTerm)}`),
    };
}
